import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import isEqual from 'lodash.isequal';

// Types
import { AppState } from 'services/types';
import {
    VirtualizedTableColumnKey,
    VirtualizedTableColumnWidth,
    VirtualizedTableColumns,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import { updateQueryString } from 'services/router/actions';

// Global Actions
import { setColumnWidth } from 'services/columnWidths/actions';

// Global Components
import VirtualizedTable from 'components/VirtualizedTable';

import { SPRAY_EVENTS_LOAD_NAMESPACE } from 'services/sprayEvents/constants';

import { SprayEventUuid } from 'services/sprayEvents/types';
import {
    selectSprayEventsTableColumns,
    makeSelectSprayEventItems,
    makeSelectSprayEventsTableIsSelectable
} from './selectors';
import { EDIT_SPRAY_EVENT_MODAL } from '../../constants';

interface OwnProps {
    tableId: string;
}

interface ConnectedProps {
    items: VirtualizedTableItem[];
    columns: VirtualizedTableColumns;
    selectable: boolean;
}

interface DispatchProps {
    setColumnWidth: typeof setColumnWidth;
    updateQueryString: typeof updateQueryString;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class SprayEventsVirtualizedTable extends React.Component<Props> {
    shouldComponentUpdate(nextProps: Props): boolean {
        return !isEqual(this.props, nextProps);
    }

    handleColumnResized = (
        columnKey: VirtualizedTableColumnKey,
        columnWidth: VirtualizedTableColumnWidth
    ): void => {
        const { tableId, ...props } = this.props;
        props.setColumnWidth(tableId, columnKey, columnWidth);
    };

    handleOnExpand = (sprayEventUuid: SprayEventUuid): void => {
        const { ...props } = this.props;

        props.updateQueryString({
            [EDIT_SPRAY_EVENT_MODAL]: true,
            sprayEventUuid
        });
    };

    renderVirtualizedTable = ({ height, width }: Size): React.ReactElement => {
        const { tableId, items, columns, selectable } = this.props;

        return (
            <React.Fragment>
                <VirtualizedTable
                    id={tableId}
                    height={height}
                    width={width}
                    columns={columns}
                    items={items}
                    onColumnResized={this.handleColumnResized}
                    onExpand={this.handleOnExpand}
                    labelPlural="Spray Events"
                    editable={selectable}
                    selectable={selectable}
                    loadNamespace={SPRAY_EVENTS_LOAD_NAMESPACE}
                />
            </React.Fragment>
        );
    };

    render(): React.ReactElement {
        return (
            <AutoSizer>
                {(size: Size): React.ReactElement =>
                    this.renderVirtualizedTable(size)
                }
            </AutoSizer>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    items: makeSelectSprayEventItems(),
    columns: selectSprayEventsTableColumns,
    selectable: makeSelectSprayEventsTableIsSelectable()
});

export default connect(mapStateToProps, { setColumnWidth, updateQueryString })(
    SprayEventsVirtualizedTable
);
